import React, { ComponentType, PropsWithChildren, ImgHTMLAttributes, useEffect } from 'react';
import { loadImage } from '../../utils/dom';
import { useImageViewConfig } from './ImageViewConfig';
import { useAsync } from '../../hooks/use-async';

export interface PropsImageView
    extends Omit<
        ImgHTMLAttributes<HTMLImageElement>,
        /**
         * Since `srcSet` makes image loading complex, ImageView will drop support for it.
         * @TODO to support `srcSet` and `sizes` later. Maybe another component
         */
        'srcSet' | 'sizes'
    > {
    /** times to try loading image resource again. `2` more times by default. */
    retries?: number;
    /** url of image to load after `src` loading failed */
    fallback?: string;
    /** A component to render fallback image, ImageView by default. */
    renderFallback?: ComponentType<PropsWithChildren<ImgHTMLAttributes<HTMLImageElement>>>;
}

/**
 * A drop-in replacement component of `<img>` with loading retries and fallback support.
 * All properties besides `srcSet`/`sizes` of `<img>` supported.
 * Most of the properties can be provided by `<ImageViewConfig>` provider.
 *
 * @example Basic usage
 * // Try loading `https://v.ringcentral.com/static/logo.png` for 3 times and then fallback to `/static/logo.png`
 * <ImageView src="https://v.ringcentral.com/static/logo.png" fallback="/static/logo.png" crossOrigin="anonymous" />
 *
 * @example Configure ImageView component with `<ImageViewConfig>` provider
 * // Code in this example behaves almost the same as that in the example "Basic usage"
 * <ImageViewConfig crossOrigin="anonymous">
 *   <ImageView src="https://v.ringcentral.com/static/logo.png" fallback="/static/logo.png" />
 * </ImageViewConfig>
 */
export function ImageView(props: PropsImageView) {
    const _props = { ...useImageViewConfig(), ...props };
    const { fallback, renderFallback, retries, ...imgProps } = _props;
    const tryTimes = 1 + (retries === 0 ? 0 : retries || 2);
    const { src, crossOrigin, referrerPolicy } = imgProps;
    const pImageLoader = useAsync(async () => {
        let error;
        for (let i = 0; i < tryTimes; i++) {
            try {
                return await loadImage({ src: src || '', crossOrigin, referrerPolicy });
            } catch (e) {
                error = e;
            }
        }

        throw error;
    });

    useEffect(() => {
        pImageLoader.execute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, crossOrigin, referrerPolicy]);

    if (pImageLoader.isFailure) {
        if (fallback || renderFallback) {
            const RenderFallback = renderFallback || ImageView;

            return <RenderFallback {...imgProps} src={fallback} />;
        } else {
            return <img alt='' {...imgProps} src={src} />;
        }
    }

    if (pImageLoader.value) {
        const imgEl = pImageLoader.value;
        return (
            <img
                alt=''
                {...imgProps}
                src={imgEl.currentSrc || imgEl.src || ''}
                crossOrigin={imgEl.crossOrigin as ImgHTMLAttributes<never>['crossOrigin']}
                referrerPolicy={imgEl.referrerPolicy as ImgHTMLAttributes<never>['referrerPolicy']}
            />
        );
    }

    return null;
}
